import { Plus } from 'lucide-react';
import * as React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PageBody, PageHeader } from '@/components/layout/page-header';
import { CarregarMais } from '@/components/patterns/carregar-mais';
import { FilterBar, FilterBusca, FilterChips, FilterLimpar } from '@/components/patterns/filter-bar';
import { ChipPeriodo, FiltroPeriodo } from '@/components/patterns/filtro-periodo';
import { PermissionButton } from '@/components/patterns/permission-button';
import { EstadoErroOuIndisponivel, EstadoVazio } from '@/components/patterns/states';
import { Card } from '@/components/ui/card';
import { Field, Input } from '@/components/ui/field';
import { Skeleton } from '@/components/ui/skeleton';
import type { FiltrosIncidentes, IncidenteResumo, SeveridadeIncidente, StatusIncidente } from '@/data/types';
import { SEVERIDADES, STATUS_INCIDENTE } from '@/data/types';
import { useIncidentes } from '@/hooks/use-incidentes';
import { useSessao } from '@/hooks/use-sessao';
import { plural } from '@/lib/format';
import { periodoDaUrl } from '@/lib/periodo';
import { COR_SEVERIDADE, podeEscreverIncidente, ROTULO_SEVERIDADE, ROTULO_STATUS_INCIDENTE } from '@/lib/incidentes';
import { cn } from '@/lib/utils';
import { IncidenteCard } from './incidente-card';
import { IncidenteDialog } from './IncidenteDialog';
import { IncidenteSheet } from './IncidenteSheet';

/**
 * Incidentes — bugs conhecidos por versão, em estilo feed.
 *
 * Filtros (status e severidade múltiplos, versão, busca por título) e o
 * incidente aberto vivem na URL: `?status=aberto&status=mitigado&severidade=alta&versao=…&q=…&incidente=7`.
 * `/incidentes/:id` abre a mesma visão de detalhe como link direto.
 */

const ATRASO_BUSCA_MS = 300;

function lerLista<T extends string>(params: URLSearchParams, chave: string, validos: T[]): T[] {
  return params.getAll(chave).filter((valor): valor is T => validos.includes(valor as T));
}

export function IncidentesPage() {
  const [params, setParams] = useSearchParams();
  const { id: idDaRota } = useParams();
  const navegar = useNavigate();
  const { roles } = useSessao();
  const escrita = podeEscreverIncidente(roles);

  const statusParam = params.getAll('status').join(',');
  const severidadeParam = params.getAll('severidade').join(',');
  // Memo pela string: `getAll` devolve array novo a cada render e a lista recomeçaria do topo.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const status = React.useMemo(() => lerLista<StatusIncidente>(params, 'status', STATUS_INCIDENTE), [statusParam]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const severidade = React.useMemo(() => lerLista<SeveridadeIncidente>(params, 'severidade', SEVERIDADES), [severidadeParam]);
  const deParam = params.get('de');
  const ateParam = params.get('ate');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const periodo = React.useMemo(() => periodoDaUrl(params), [deParam, ateParam]);
  const versao = params.get('versao') ?? '';
  const q = params.get('q') ?? '';

  const idBruto = idDaRota ?? params.get('incidente');
  const idAberto = idBruto && /^\d+$/.test(idBruto) ? Number(idBruto) : null;

  const [busca, setBusca] = React.useState(q);
  const [versaoDigitada, setVersaoDigitada] = React.useState(versao);
  const [registrando, setRegistrando] = React.useState(false);
  React.useEffect(() => setBusca(q), [q]);
  React.useEffect(() => setVersaoDigitada(versao), [versao]);

  function atualizar(mudancas: Record<string, string | string[] | null>) {
    setParams(
      (atual) => {
        const proximo = new URLSearchParams(atual);
        for (const [chave, valor] of Object.entries(mudancas)) {
          proximo.delete(chave);
          if (Array.isArray(valor)) valor.forEach((item) => proximo.append(chave, item));
          else if (valor) proximo.set(chave, valor);
        }
        return proximo;
      },
      { replace: true },
    );
  }

  React.useEffect(() => {
    if (busca.trim() === q) return;
    const idTempo = window.setTimeout(() => atualizar({ q: busca.trim() || null }), ATRASO_BUSCA_MS);
    return () => window.clearTimeout(idTempo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca]);

  React.useEffect(() => {
    if (versaoDigitada.trim() === versao) return;
    const idTempo = window.setTimeout(() => atualizar({ versao: versaoDigitada.trim() || null }), ATRASO_BUSCA_MS);
    return () => window.clearTimeout(idTempo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versaoDigitada]);

  function abrir(incidente: IncidenteResumo) {
    if (idDaRota) {
      navegar(`/incidentes/${incidente.id}${params.toString() ? `?${params.toString()}` : ''}`);
      return;
    }
    setParams((atual) => {
      const proximo = new URLSearchParams(atual);
      proximo.set('incidente', String(incidente.id));
      return proximo;
    });
  }

  function fechar() {
    if (idDaRota) navegar(`/incidentes${params.toString() ? `?${params.toString()}` : ''}`, { replace: true });
    else atualizar({ incidente: null });
  }

  const filtros: FiltrosIncidentes = React.useMemo(
    () => ({ status, severidade, versao: versao || null, q: q || null, criado_de: periodo.de, criado_ate: periodo.ate }),
    [status, severidade, versao, q, periodo],
  );
  const lista = useIncidentes(filtros);

  const quantidadeFiltros = [status.length ? 's' : null, severidade.length ? 'v' : null, versao, q, periodo.de || periodo.ate ? 'p' : null].filter(Boolean).length;

  return (
    <PageBody className="lg:max-w-4xl">
      <PageHeader
        eyebrow="Compilações"
        titulo="Incidentes"
        descricao="Bugs conhecidos por versão. Quem homologa é avisado quando a versão tem incidente aberto, e o feed destaca as versões afetadas."
        acao={
          <PermissionButton
            permitido={escrita.permitido}
            motivo={escrita.motivo}
            variant="primary"
            onClick={() => setRegistrando(true)}
          >
            <Plus aria-hidden="true" />
            Registrar incidente
          </PermissionButton>
        }
      />

      <Card as="section" aria-label="Filtros dos incidentes" className="px-4 py-3">
        <FilterBar>
          <FilterBusca
            id="inc-busca"
            label="Buscar no título"
            valor={busca}
            aoMudar={setBusca}
            placeholder="NF-e, Persona…"
            className="min-w-48"
          />
          <Field id="inc-versao" label="Versão afetada" className="min-w-44 max-w-xs flex-1">
            {(props) => (
              <Input
                {...props}
                value={versaoDigitada}
                onChange={(evento) => setVersaoDigitada(evento.target.value)}
                placeholder="2.2602.78.0"
                className="font-mono"
              />
            )}
          </Field>
          <FiltroPeriodo
            id="inc-periodo"
            de={periodo.de}
            ate={periodo.ate}
            aoMudar={(p) => atualizar({ de: p.de, ate: p.ate })}
          />
          <FilterChips
            id="inc-status"
            compacto
            label="Estado"
            valores={status}
            aoMudar={(valores) => atualizar({ status: valores })}
            opcoes={STATUS_INCIDENTE.map((valor) => ({ valor, rotulo: ROTULO_STATUS_INCIDENTE[valor] }))}
          />
          <FilterChips
            id="inc-severidade"
            compacto
            label="Severidade"
            valores={severidade}
            aoMudar={(valores) => atualizar({ severidade: valores })}
            opcoes={[...SEVERIDADES].reverse().map((valor) => ({
              valor,
              rotulo: ROTULO_SEVERIDADE[valor],
              icone: <span aria-hidden="true" className={cn('size-2 rounded-full bg-current', COR_SEVERIDADE[valor])} />,
            }))}
          />
          <FilterLimpar
            ativo={quantidadeFiltros > 0}
            quantidade={quantidadeFiltros}
            aoLimpar={() => {
              setBusca('');
              setVersaoDigitada('');
              atualizar({ status: null, severidade: null, versao: null, q: null, de: null, ate: null });
            }}
          />
        </FilterBar>
        {periodo.de || periodo.ate ? (
          <div className="mt-3 border-t border-border-subtle pt-3">
            <ChipPeriodo de={periodo.de} ate={periodo.ate} aoLimpar={() => atualizar({ de: null, ate: null })} />
          </div>
        ) : null}
      </Card>

      <p className="sr-only" role="status" aria-live="polite">
        {lista.estado === 'pronto' ? `${plural(lista.itens.length, 'incidente carregado', 'incidentes carregados')}.` : ''}
      </p>

      {lista.estado === 'carregando' ? (
        <div className="flex flex-col gap-2.5" role="status" aria-label="Carregando incidentes">
          {Array.from({ length: 4 }).map((_, indice) => (
            <Skeleton key={indice} className="h-32 rounded-card" />
          ))}
        </div>
      ) : lista.estado === 'erro' ? (
        <Card>
          <EstadoErroOuIndisponivel erro={lista.erro} aoTentarDeNovo={lista.recarregar} />
        </Card>
      ) : lista.itens.length === 0 ? (
        <Card>
          <EstadoVazio
            titulo={quantidadeFiltros > 0 ? 'Nenhum incidente com estes filtros' : 'Nenhum incidente registrado'}
            descricao={
              quantidadeFiltros > 0
                ? 'Afrouxe a busca ou limpe os filtros para ver mais incidentes.'
                : 'Quando alguém registrar um bug conhecido de uma versão, ele aparece aqui.'
            }
          />
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          <ul className="flex flex-col gap-2.5" aria-label="Incidentes">
            {lista.itens.map((incidente) => (
              <li key={incidente.id}>
                <IncidenteCard incidente={incidente} ativo={incidente.id === idAberto} aoAbrir={abrir} />
              </li>
            ))}
          </ul>
          <CarregarMais
            temMais={lista.temMais}
            carregando={lista.carregandoMais}
            erro={lista.erroMais}
            aoCarregar={lista.carregarMais}
            total={lista.itens.length}
            singular="incidente carregado"
            plural_="incidentes carregados"
            fim="Fim da lista"
            rotuloCarregando="Carregando incidentes…"
            mensagemErro="Não foi possível carregar mais incidentes"
          />
        </div>
      )}

      <IncidenteSheet id={idAberto} aoFechar={fechar} mostrarAbrirEmPagina={!idDaRota} />

      <IncidenteDialog
        aberto={registrando}
        aoFechar={() => setRegistrando(false)}
        aoSalvar={(detalhe) => {
          setRegistrando(false);
          abrir(detalhe);
        }}
      />
    </PageBody>
  );
}
